import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Text,
  Button,
  Stack,
  Group,
  Loader,
  Alert,
  Textarea,
  Divider,
  Card,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  TextInput,
  Select,
  Paper,
  Title,
  Avatar,
  Dialog
} from '@mantine/core'
import {
  FiArrowLeft,
  FiHeart,
  FiShare2,
  FiTrash2,
  FiMessageSquare,
  FiCornerDownRight,
  FiSend,
  FiInfo,
  FiEdit2,
  FiExternalLink,
  FiSearch,
  FiX,
  FiTarget,
  FiPackage,
  FiUser,
  FiCalendar
} from 'react-icons/fi'
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import type { SlopPostDto, SlopPostCommentDto } from '../../app/models/slopPost'
import type { FirearmModel } from '../../app/models/firearmModel'
import { store } from '../../app/stores/store'

interface EditFormState {
  title: string
  description: string
  firearmModelId: string | null
  cartridgeSpecId: string | null
}

function formatDate (date: string) {
  const d = new Date(date)
  const diffMs = Date.now() - d.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return d.toLocaleDateString()
}

function formatAbsoluteDate (date: string) {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric'
  })
}

function initials (username: string | null | undefined) {
  if (!username) return '?'
  return username.slice(0, 2).toUpperCase()
}

export default function SlopPostPage () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const currentUser = store.userStore.user
  const isAdmin = currentUser?.isAdmin ?? false
  const isLoggedIn = !!currentUser

  const [post, setPost] = useState<SlopPostDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [comments, setComments] = useState<SlopPostCommentDto[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [replyingToId, setReplyingToId] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const [shareCopied, setShareCopied] = useState(false)

  // Delete + edit modals
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<EditFormState>({
    title: '', description: '', firearmModelId: null, cartridgeSpecId: null
  })

  // Picker data (loaded lazily when edit modal opens)
  const [firearmModels, setFirearmModels] = useState<FirearmModel[]>([])
  const [cartridgeOptions, setCartridgeOptions] = useState<{ value: string; label: string }[]>([])
  const [pickersLoading, setPickersLoading] = useState(false)
  const [firearmSearch, setFirearmSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    agent.SlopFeed.getDetails(id)
      .then((p) => { setPost(p) })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!post) return
    setCommentsLoading(true)
    agent.SlopFeed.getComments(post.id)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setCommentsLoading(false))
  }, [post?.id])

  // Lazy-load firearm and cartridge lists once edit modal opens
  useEffect(() => {
    if (!editOpen || firearmModels.length > 0) return
    let cancelled = false
    setPickersLoading(true)
    void (async () => {
      try {
        const [fm, cs] = await Promise.all([
          agent.FirearmModels.list({ pageSize: 100 }),
          agent.CartridgeSpecs.list({ pageSize: 80 })
        ])
        if (cancelled) return
        setFirearmModels(fm.items)
        setCartridgeOptions(cs.items.map((c) => ({
          value: c.id,
          label: (c.shortName || c.name || '').trim() || c.id
        })))
      } catch {
        if (!cancelled) { setFirearmModels([]); setCartridgeOptions([]) }
      } finally {
        if (!cancelled) setPickersLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [editOpen])

  const canManage = useMemo(() => {
    if (!post || !currentUser) return false
    return post.userId === currentUser.id || isAdmin
  }, [post, currentUser, isAdmin])

  const requireAuth = (action: () => void) => {
    if (!isLoggedIn) { history.push('/register'); return }
    action()
  }

  const handleToggleLike = async () => {
    if (!post) return
    const result = await agent.SlopFeed.toggleLike(post.id)
    setPost({ ...post, likeCount: result.newLikeCount, isLikedByCurrentUser: result.isLiked })
  }

  const handleShare = async () => {
    if (!post) return
    const url = `${window.location.origin}/slop-feed/${post.id}`
    if (navigator.share) {
      try { await navigator.share({ title: post.title || 'GunSlop Video', url }) } catch {}
    } else {
      await navigator.clipboard.writeText(url)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    }
  }

  const handleDelete = async () => {
    if (!post) return
    setDeleteLoading(true)
    try {
      await agent.SlopFeed.deletePost(post.id)
      history.push('/slop-feed')
    } catch {
      setDeleteLoading(false)
      setDeleteConfirmOpen(false)
    }
  }

  const openEditModal = () => {
    if (!post) return
    setEditForm({
      title: post.title ?? '',
      description: post.description ?? '',
      firearmModelId: post.firearmModelId ?? null,
      cartridgeSpecId: post.cartridgeSpecId ?? null
    })
    setEditError(null)
    setFirearmSearch('')
    setEditOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!post) return
    if (!editForm.title.trim()) {
      setEditError('Title is required')
      return
    }
    setEditSaving(true)
    setEditError(null)
    try {
      const updated = await agent.SlopFeed.updatePost(post.id, {
        title: editForm.title.trim(),
        description: editForm.description,
        firearmModelId: editForm.firearmModelId ?? '',
        cartridgeSpecId: editForm.cartridgeSpecId ?? '',
        clearFirearmLink: !editForm.firearmModelId,
        clearCartridgeLink: !editForm.cartridgeSpecId
      })
      setPost((prev) => prev ? {
        ...prev,
        title: updated.title,
        description: updated.description,
        firearmModelId: updated.firearmModelId,
        firearmModelName: updated.firearmModelName,
        cartridgeSpecId: updated.cartridgeSpecId,
        cartridgeLabel: updated.cartridgeLabel,
        linkedCaption: updated.linkedCaption
      } : prev)
      setEditOpen(false)
    } catch (err: any) {
      setEditError(err?.message || 'Failed to save changes')
    } finally {
      setEditSaving(false)
    }
  }

  const handleAddComment = async () => {
    if (!post || !newComment.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(post.id, newComment.trim())
      setComments((prev) => [...prev, created])
      setNewComment('')
      setPost((p) => p ? { ...p, commentCount: p.commentCount + 1 } : p)
    } catch { } finally { setCommentSubmitting(false) }
  }

  const handleAddReply = async (parentId: number) => {
    if (!post || !replyText.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(post.id, replyText.trim(), parentId)
      setComments((prev) =>
        prev.map((c) => c.id === parentId ? { ...c, replies: [...c.replies, created] } : c)
      )
      setReplyText(''); setReplyingToId(null)
      setPost((p) => p ? { ...p, commentCount: p.commentCount + 1 } : p)
    } catch { } finally { setCommentSubmitting(false) }
  }

  const handleDeleteComment = async (commentId: number, parentId?: number) => {
    if (!post) return
    try {
      await agent.SlopFeed.deleteComment(post.id, commentId)
      if (parentId != null) {
        setComments((prev) =>
          prev.map((c) => c.id === parentId ? { ...c, replies: c.replies.filter((r) => r.id !== commentId) } : c)
        )
      } else {
        setComments((prev) => prev.filter((c) => c.id !== commentId))
      }
      setPost((p) => p ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p)
    } catch { }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Loader />
      </Box>
    )
  }

  if (notFound || !post) {
    return (
      <Box sx={{ maxWidth: 700, margin: '0 auto', padding: '40px 24px' }}>
        <Text sx={{ color: 'var(--gs-muted)', textAlign: 'center' }}>Video not found.</Text>
        <Group position="center" mt="md">
          <Button variant="subtle" leftIcon={<FiArrowLeft />} onClick={() => history.push('/slop-feed')}>
            Back to Slop Feed
          </Button>
        </Group>
      </Box>
    )
  }

  const firearmModelHref = post.firearmModelId ? `/registry/${post.firearmModelId}` : null
  const cartridgeHref = post.cartridgeSpecId ? `/ammo/specs/${post.cartridgeSpecId}` : null
  const hasGear = !!(post.firearmModelId || post.cartridgeSpecId)

  return (
    <Box sx={{ maxWidth: 940, margin: '0 auto', padding: '24px', '@media (max-width: 575px)': { padding: '12px 14px' } }}>
      {/* Top breadcrumb */}
      <Group position="apart" mb="md">
        <Button
          variant="subtle"
          size="sm"
          leftIcon={<FiArrowLeft size={14} />}
          onClick={() => history.push('/slop-feed')}
          sx={{ color: 'var(--gs-muted)', paddingLeft: 0 }}
        >
          Slop Feed
        </Button>
        {canManage && (
          <Group spacing={6}>
            {isAdmin && post.userId !== currentUser?.id && (
              <Badge color="orange" variant="filled" size="sm" sx={{ fontFamily: 'DM Mono, monospace' }}>
                Admin
              </Badge>
            )}
            <Tooltip label="Edit post" withinPortal>
              <ActionIcon
                variant="light"
                size="lg"
                onClick={openEditModal}
                aria-label="Edit post"
              >
                <FiEdit2 size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete post" withinPortal>
              <ActionIcon
                color="red"
                variant="light"
                size="lg"
                onClick={() => setDeleteConfirmOpen(true)}
                aria-label="Delete post"
              >
                <FiTrash2 size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </Group>

      {/* Video */}
      <Card
        withBorder
        radius="md"
        p={0}
        sx={{
          background: '#000',
          overflow: 'hidden',
          borderColor: 'var(--gs-border)',
          marginBottom: 20
        }}
      >
        {post.isEmbedded
          ? (
            <Box sx={{ paddingBottom: '56.25%', position: 'relative' }}>
              <iframe
                src={post.embedSourceUrl ?? ''}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allowFullScreen
                title={post.title ?? 'Embedded video'}
              />
            </Box>
            )
          : (
            <video
              controls
              poster={post.thumbnailUrl ? toAbsoluteMediaUrl(post.thumbnailUrl) : undefined}
              style={{ width: '100%', display: 'block', maxHeight: 560, background: '#000' }}
            >
              <source src={toAbsoluteMediaUrl(post.videoUrl)} />
            </video>
            )}
      </Card>

      {/* Title + author + actions card */}
      <Card withBorder radius="md" p="md" sx={{ background: 'var(--gs-surface2)', borderColor: 'var(--gs-border)', marginBottom: 16 }}>
        <Stack gap="sm">
          <Group position="apart" align="flex-start" noWrap sx={{ flexWrap: 'wrap', gap: 12 }}>
            <Stack gap={4} sx={{ minWidth: 0, flex: 1 }}>
              <Title order={2}
                sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 26, letterSpacing: 1, lineHeight: 1.2, wordBreak: 'break-word' }}>
                {post.title || 'Untitled'}
              </Title>
              <Group spacing={10} sx={{ flexWrap: 'wrap' }}>
                <Group spacing={6}>
                  <Avatar size={26} radius="xl" color="orange">{initials(post.username)}</Avatar>
                  <Text
                    component={RouterLink}
                    to={post.username ? `/profiles/${post.username}` : '#'}
                    sx={{ fontSize: 13, fontWeight: 600, color: 'var(--gs-accent)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {post.username || 'unknown'}
                  </Text>
                </Group>
                <Tooltip label={formatAbsoluteDate(post.createdAt)} withinPortal>
                  <Group spacing={4} sx={{ color: 'var(--gs-muted)' }}>
                    <FiCalendar size={12} />
                    <Text size="xs">{formatDate(post.createdAt)}</Text>
                  </Group>
                </Tooltip>
                {post.isEmbedded && (
                  <Badge size="sm" variant="light" color="gray">Embedded</Badge>
                )}
                {post.fileSizeMb != null && (
                  <Badge size="sm" variant="light" color="gray" sx={{ fontFamily: 'DM Mono, monospace' }}>
                    {post.fileSizeMb} MB
                  </Badge>
                )}
              </Group>
            </Stack>

            <Group spacing={6}>
              <Tooltip label={post.isLikedByCurrentUser ? 'Unlike' : 'Like'} withinPortal>
                <Button
                  variant="light"
                  size="sm"
                  leftIcon={<FiHeart size={14} fill={post.isLikedByCurrentUser ? 'currentColor' : 'none'} />}
                  onClick={() => requireAuth(handleToggleLike)}
                  sx={{
                    color: post.isLikedByCurrentUser ? 'var(--gs-accent)' : 'inherit',
                    background: post.isLikedByCurrentUser ? 'rgba(232,93,4,0.10)' : undefined
                  }}
                >
                  {post.likeCount}
                </Button>
              </Tooltip>
              <Tooltip label={shareCopied ? 'Copied!' : 'Copy link'} withinPortal>
                <Button
                  variant="light"
                  size="sm"
                  leftIcon={<FiShare2 size={14} />}
                  onClick={handleShare}
                >
                  {shareCopied ? 'Copied' : 'Share'}
                </Button>
              </Tooltip>
            </Group>
          </Group>

          {post.description && (
            <Text sx={{ fontSize: 14, color: 'var(--gs-text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
              {post.description}
            </Text>
          )}
        </Stack>
      </Card>

      {/* Featured Gear */}
      {hasGear && (
        <Card withBorder radius="md" p="md" sx={{ background: 'var(--gs-surface2)', borderColor: 'var(--gs-border)', marginBottom: 16 }}>
          <Group spacing={8} mb="xs" sx={{ color: 'var(--gs-muted)' }}>
            <FiTarget size={14} />
            <Text size="xs" sx={{ textTransform: 'uppercase', letterSpacing: 1.4, fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>
              Featured Gear
            </Text>
          </Group>

          <Stack gap="sm">
            {firearmModelHref && (
              <Paper
                component={RouterLink}
                to={firearmModelHref}
                withBorder
                p="sm"
                sx={{
                  display: 'block',
                  background: 'var(--gs-surface1)',
                  borderColor: 'var(--gs-border)',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'border-color 120ms ease, transform 120ms ease',
                  '&:hover': { borderColor: 'var(--gs-accent)', transform: 'translateY(-1px)' }
                }}
              >
                <Group position="apart" align="center" noWrap>
                  <Group spacing={10} sx={{ minWidth: 0 }} noWrap>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(232,93,4,0.12)', color: 'var(--gs-accent)',
                      flexShrink: 0
                    }}>
                      <FiTarget size={18} />
                    </Box>
                    <Stack gap={0} sx={{ minWidth: 0 }}>
                      <Text size="xs" sx={{ color: 'var(--gs-muted)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                        Firearm
                      </Text>
                      <Text sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word' }}>
                        {post.firearmModelName || 'Linked firearm'}
                      </Text>
                    </Stack>
                  </Group>
                  <Group spacing={4} sx={{ color: 'var(--gs-muted)', flexShrink: 0 }}>
                    <Text size="xs">View in registry</Text>
                    <FiExternalLink size={14} />
                  </Group>
                </Group>
              </Paper>
            )}

            {cartridgeHref && (
              <Paper
                component={RouterLink}
                to={cartridgeHref}
                withBorder
                p="sm"
                sx={{
                  display: 'block',
                  background: 'var(--gs-surface1)',
                  borderColor: 'var(--gs-border)',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'border-color 120ms ease, transform 120ms ease',
                  '&:hover': { borderColor: 'var(--gs-accent)', transform: 'translateY(-1px)' }
                }}
              >
                <Group position="apart" align="center" noWrap>
                  <Group spacing={10} sx={{ minWidth: 0 }} noWrap>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(232,93,4,0.12)', color: 'var(--gs-accent)',
                      flexShrink: 0
                    }}>
                      <FiPackage size={18} />
                    </Box>
                    <Stack gap={0} sx={{ minWidth: 0 }}>
                      <Text size="xs" sx={{ color: 'var(--gs-muted)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                        Cartridge
                      </Text>
                      <Text sx={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word' }}>
                        {post.cartridgeLabel || 'Linked cartridge'}
                      </Text>
                    </Stack>
                  </Group>
                  <Group spacing={4} sx={{ color: 'var(--gs-muted)', flexShrink: 0 }}>
                    <Text size="xs">View specs</Text>
                    <FiExternalLink size={14} />
                  </Group>
                </Group>
              </Paper>
            )}
          </Stack>
        </Card>
      )}

      {/* If no gear and the user can edit, suggest adding one */}
      {!hasGear && canManage && (
        <Alert
          icon={<FiInfo size={14} />}
          mb="md"
          sx={{ fontSize: 13 }}
          color="gray"
        >
          No firearm or cartridge linked.{' '}
          <Text component="a" onClick={openEditModal} sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}>
            Add a link
          </Text>
          {' '}so viewers can jump to the registry.
        </Alert>
      )}

      {/* Comments */}
      <Card withBorder radius="md" p="md" sx={{ background: 'var(--gs-surface2)', borderColor: 'var(--gs-border)' }}>
        <Stack gap="md">
          <Group spacing={6}>
            <FiMessageSquare size={14} />
            <Text sx={{ fontSize: 15, fontWeight: 600 }}>
              {post.commentCount} Comment{post.commentCount !== 1 ? 's' : ''}
            </Text>
          </Group>

          {isLoggedIn
            ? (
              <Group align="flex-end" spacing="xs" sx={{ flexWrap: 'nowrap' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Textarea
                    placeholder="Add a comment…"
                    value={newComment}
                    onChange={(e) => setNewComment(e.currentTarget.value)}
                    autosize minRows={1} maxRows={4}
                    disabled={commentSubmitting}
                  />
                </Box>
                <Button
                  variant="light"
                  size="sm"
                  leftIcon={<FiSend size={14} />}
                  loading={commentSubmitting}
                  disabled={!newComment.trim()}
                  onClick={handleAddComment}
                >
                  Post
                </Button>
              </Group>
              )
            : (
              <Alert icon={<FiInfo size={14} />} sx={{ fontSize: 13 }}>
                <Text
                  component="a"
                  onClick={() => history.push('/register')}
                  sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                >Sign up</Text>{' '}or{' '}
                <Text
                  component="a"
                  onClick={() => history.push('/login')}
                  sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                >log in</Text>{' '}to comment.
              </Alert>
              )}

          {commentsLoading
            ? (
              <Loader size="sm" style={{ display: 'block', margin: '0 auto' }} />
              )
            : comments.length === 0
              ? (
                <Text sx={{ fontSize: 13, color: 'var(--gs-muted)', textAlign: 'center', padding: '8px 0' }}>
                  No comments yet. Be the first!
                </Text>
                )
              : (
                <Stack gap="sm">
                  {comments.map((comment) => (
                    <Box key={comment.id}>
                      <Box sx={{ background: 'var(--gs-surface1)', borderRadius: 6, padding: '10px 12px' }}>
                        <Group position="apart" align="flex-start">
                          <Box sx={{ flex: 1 }}>
                            <Group spacing="xs">
                              <Avatar size={20} radius="xl" color="orange">{initials(comment.authorUsername)}</Avatar>
                              <Text sx={{ fontSize: 12, fontWeight: 600, color: 'var(--gs-accent)' }}>
                                {comment.authorUsername}
                              </Text>
                              <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                                {formatDate(comment.createdAt)}
                              </Text>
                            </Group>
                            <Text sx={{ fontSize: 13, marginTop: 4, wordBreak: 'break-word' }}>
                              {comment.body}
                            </Text>
                          </Box>
                          <Group spacing={4}>
                            {isLoggedIn && (
                              <Button
                                variant="subtle" size="xs" compact
                                leftIcon={<FiCornerDownRight size={12} />}
                                sx={{ color: 'var(--gs-muted)', fontSize: 11 }}
                                onClick={() => setReplyingToId(replyingToId === comment.id ? null : comment.id)}
                              >Reply</Button>
                            )}
                            {(comment.authorUsername === currentUser?.userName || isAdmin) && (
                              <Tooltip label="Delete comment" withinPortal>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  sx={{ color: 'var(--gs-muted)' }}
                                  onClick={() => handleDeleteComment(comment.id)}
                                  aria-label="Delete comment"
                                >
                                  <FiTrash2 size={12} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Group>

                        {replyingToId === comment.id && (
                          <Group align="flex-end" spacing="xs" sx={{ marginTop: 8 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Textarea
                                placeholder={`Reply to ${comment.authorUsername}…`}
                                value={replyText}
                                onChange={(e) => setReplyText(e.currentTarget.value)}
                                autosize minRows={1} maxRows={3}
                                disabled={commentSubmitting}
                              />
                            </Box>
                            <Button
                              variant="light" size="xs"
                              leftIcon={<FiSend size={12} />}
                              loading={commentSubmitting}
                              disabled={!replyText.trim()}
                              onClick={() => handleAddReply(comment.id)}
                            >Reply</Button>
                          </Group>
                        )}
                      </Box>

                      {comment.replies.length > 0 && (
                        <Stack gap={4} sx={{ marginLeft: 24, marginTop: 4 }}>
                          {comment.replies.map((reply) => (
                            <Box
                              key={reply.id}
                              sx={{
                                background: 'var(--gs-surface1)',
                                borderRadius: 6,
                                padding: '8px 12px',
                                borderLeft: '2px solid var(--gs-border)'
                              }}
                            >
                              <Group position="apart" align="flex-start">
                                <Box sx={{ flex: 1 }}>
                                  <Group spacing="xs">
                                    <Avatar size={18} radius="xl" color="orange">{initials(reply.authorUsername)}</Avatar>
                                    <Text sx={{ fontSize: 12, fontWeight: 600, color: 'var(--gs-accent)' }}>
                                      {reply.authorUsername}
                                    </Text>
                                    <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                                      {formatDate(reply.createdAt)}
                                    </Text>
                                  </Group>
                                  <Text sx={{ fontSize: 13, marginTop: 4, wordBreak: 'break-word' }}>
                                    {reply.body}
                                  </Text>
                                </Box>
                                {(reply.authorUsername === currentUser?.userName || isAdmin) && (
                                  <Tooltip label="Delete reply" withinPortal>
                                    <ActionIcon
                                      variant="subtle"
                                      size="sm"
                                      sx={{ color: 'var(--gs-muted)' }}
                                      onClick={() => handleDeleteComment(reply.id, comment.id)}
                                      aria-label="Delete reply"
                                    >
                                      <FiTrash2 size={12} />
                                    </ActionIcon>
                                  </Tooltip>
                                )}
                              </Group>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
                )}
        </Stack>
      </Card>

      {/* Edit modal */}
      <Modal
        opened={editOpen}
        onClose={() => { if (!editSaving) setEditOpen(false) }}
        title="Edit video"
        size="lg"
        styles={{
          content: {
            '@media (max-width: 575px)': { margin: 0, borderRadius: 0, maxHeight: '100dvh', overflowY: 'auto' }
          }
        }}
      >
        <Stack gap="md">
          {editError && (
            <Alert icon={<FiInfo size={14} />} color="red" title="Error">
              {editError}
            </Alert>
          )}

          {isAdmin && post.userId !== currentUser?.id && (
            <Alert color="orange" icon={<FiUser size={14} />} sx={{ fontSize: 13 }}>
              You are editing <strong>{post.username}</strong>'s post as an admin.
            </Alert>
          )}

          <TextInput
            label="Title"
            placeholder="What are you shooting?"
            value={editForm.title}
            onChange={(e) => setEditForm((s) => ({ ...s, title: e.currentTarget.value }))}
            disabled={editSaving}
            required
            maxLength={200}
          />

          <Textarea
            label="Description"
            placeholder="Details about the setup, specs, etc."
            value={editForm.description}
            onChange={(e) => setEditForm((s) => ({ ...s, description: e.currentTarget.value }))}
            minRows={3}
            autosize
            maxRows={10}
            disabled={editSaving}
            maxLength={5000}
          />

          {/* Firearm picker */}
          <Box>
            <Text size="sm" fw={500} mb={6}>Linked Firearm</Text>
            {pickersLoading
              ? <Loader size="sm" />
              : editForm.firearmModelId
                ? (
                  <Group spacing={6} mb={6}>
                    <Paper withBorder p={8} sx={{ background: 'var(--gs-surface1)', flex: 1 }}>
                      <Text size="sm" fw={600}>
                        {firearmModels.find(m => m.id === editForm.firearmModelId)
                          ? `${firearmModels.find(m => m.id === editForm.firearmModelId)!.make} ${firearmModels.find(m => m.id === editForm.firearmModelId)!.model}`
                          : (post.firearmModelName || editForm.firearmModelId)}
                      </Text>
                      {firearmModels.find(m => m.id === editForm.firearmModelId)?.caliber && (
                        <Text size="xs" c="dimmed">
                          {firearmModels.find(m => m.id === editForm.firearmModelId)!.caliber}
                        </Text>
                      )}
                    </Paper>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      compact
                      leftIcon={<FiX size={12} />}
                      onClick={() => setEditForm((s) => ({ ...s, firearmModelId: null }))}
                      disabled={editSaving}
                    >
                      Remove
                    </Button>
                  </Group>
                  )
                : (
                  <>
                    <TextInput
                      placeholder="Search make, model, or caliber…"
                      icon={<FiSearch size={14} />}
                      value={firearmSearch}
                      onChange={(e) => setFirearmSearch(e.currentTarget.value)}
                      disabled={editSaving}
                      mb={4}
                    />
                    {firearmSearch.trim().length > 0 && (
                      <Stack spacing={4}>
                        {firearmModels
                          .filter(m => `${m.make} ${m.model} ${m.caliber}`.toLowerCase().includes(firearmSearch.toLowerCase()))
                          .slice(0, 6)
                          .map(m => (
                            <Paper
                              key={m.id}
                              p={6}
                              withBorder
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: 'var(--gs-surface2)',
                                '&:hover': { backgroundColor: 'var(--gs-surface3)' }
                              }}
                              onClick={() => { setEditForm((s) => ({ ...s, firearmModelId: m.id })); setFirearmSearch('') }}
                            >
                              <Text size="sm">{m.make} {m.model}</Text>
                              <Text size="xs" c="dimmed">{m.caliber}</Text>
                            </Paper>
                          ))}
                        {firearmModels.filter(m => `${m.make} ${m.model} ${m.caliber}`.toLowerCase().includes(firearmSearch.toLowerCase())).length === 0 && (
                          <Text size="xs" c="dimmed">No matching firearms found</Text>
                        )}
                      </Stack>
                    )}
                  </>
                  )}
          </Box>

          {/* Cartridge picker */}
          <Select
            label="Linked Cartridge"
            placeholder={pickersLoading ? 'Loading…' : 'Select cartridge…'}
            data={cartridgeOptions}
            value={editForm.cartridgeSpecId}
            onChange={(val) => setEditForm((s) => ({ ...s, cartridgeSpecId: val }))}
            disabled={editSaving || pickersLoading}
            clearable
            searchable
            nothingFound="No cartridges"
          />

          <Divider />

          <Group position="right">
            <Button variant="default" onClick={() => setEditOpen(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              loading={editSaving}
              disabled={!editForm.title.trim()}
            >
              Save changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Dialog
        opened={deleteConfirmOpen}
        onClose={() => { if (!deleteLoading) setDeleteConfirmOpen(false) }}
        withBorder
        size="sm"
        position={{ bottom: 20, right: 20 }}
      >
        <Stack gap="md">
          <Text size="sm" fw={600}>Delete this video?</Text>
          <Text size="xs" c="dimmed">
            This permanently removes the video, its comments, and all likes. This cannot be undone.
          </Text>
          <Group position="right">
            <Button variant="default" size="xs" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button color="red" size="xs" onClick={handleDelete} loading={deleteLoading} leftIcon={<FiTrash2 size={12} />}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Dialog>
    </Box>
  )
}
